import React, { useState, useEffect, useMemo } from 'react';
import {
  Calendar, ChevronLeft, ChevronRight, Search, X, List, Eye,
  AlertCircle, CheckCircle, Info, MapPin, Clock, FileText, User
} from 'lucide-react';
import Modal from '../shared/Modal';
import SchedulingCalendar from '../shared/SchedulingCalendar';
import { withTeamSelection } from '../../lib/teamSelection';

interface Props {
  auth: any;
}

export default function ReferrerCalendarView({ auth: _auth }: Props) {
  const [events, setEvents] = useState<any[]>([]);
  const [eventsLoading, setEventsLoading] = useState(true);

  // Filter & Navigation states
  // REF-14: the default view follows the viewport — the list (agenda)
  // below md, the month grid at md and up. An explicit toggle choice
  // is remembered and wins over the viewport default from then on.
  const [viewModeChoice, setViewModeChoice] = useState<'calendar' | 'list' | null>(null);
  const [isNarrowViewport, setIsNarrowViewport] = useState<boolean>(
    () => typeof window !== 'undefined' && typeof window.matchMedia === 'function'
      ? window.matchMedia('(max-width: 767px)').matches
      : false,
  );
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    const mq = window.matchMedia('(max-width: 767px)');
    const onChange = (e: MediaQueryListEvent) => setIsNarrowViewport(e.matches);
    setIsNarrowViewport(mq.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);
  const eventViewMode: 'calendar' | 'list' = viewModeChoice ?? (isNarrowViewport ? 'list' : 'calendar');
  const [currentCalendarDate, setCurrentCalendarDate] = useState<Date>(new Date());
  const [eventSearchQuery, setEventSearchQuery] = useState('');
  const [filterEventType, setFilterEventType] = useState('All');
  const [filterEventStatus, setFilterEventStatus] = useState('All');

  // Popup & SlideIn/Detail modal states
  const [selectedEvent, setSelectedEvent] = useState<any | null>(null);
  const [datePopupEvents, setDatePopupEvents] = useState<any[]>([]);
  const [datePopupDate, setDatePopupDate] = useState<Date | null>(null);

  useEffect(() => {
    const loadEvents = async () => {
      setEventsLoading(true);
      try {
        const token = await _auth.currentUser?.getIdToken();
        const res = await fetch(withTeamSelection('/api/referrer/events'), {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (res.ok) {
          const data = await res.json();
          // Normalize and filter events
          const normalized = (data.events || [])
            .filter((e: any) => !e.archived && e.status !== 'Archived')
            .map((e: any) => {
              const evDate = e.dateTime || e.startDate || e.createdAt;
              return {
                ...e,
                eventId: e.id || e.eventId,
                dateTime: evDate,
                eventType: e.eventType || e.type || 'Event',
                status: e.status || 'Scheduled'
              };
            });
          setEvents(normalized);
        }
      } catch (err) {
        console.error('Failed to load events:', err);
      }
      setEventsLoading(false);
    };
    loadEvents();
  }, [_auth]);

  // Filtering Logic
  const filteredEvents = useMemo(() => {
    return events.filter(ev => {
      // 1. Search Query
      if (eventSearchQuery.trim()) {
        const q = eventSearchQuery.toLowerCase();
        const matchTitle = (ev.title || '').toLowerCase().includes(q);
        const matchType = (ev.eventType || '').toLowerCase().includes(q);
        const matchLocation = (ev.location || '').toLowerCase().includes(q);
        const matchDesc = (ev.description || '').toLowerCase().includes(q);
        const matchApplicant = (ev.applicantName || '').toLowerCase().includes(q);
        if (!matchTitle && !matchType && !matchLocation && !matchDesc && !matchApplicant) {
          return false;
        }
      }
      // 2. Event Type
      if (filterEventType !== 'All' && ev.eventType !== filterEventType) {
        return false;
      }
      // 3. Status
      if (filterEventStatus !== 'All' && ev.status !== filterEventStatus) {
        return false;
      }
      return true;
    });
  }, [events, eventSearchQuery, filterEventType, filterEventStatus]);

  // Format helper for listing datetime
  const formatDateTime = (val?: string) => {
    if (!val) return '—';
    return new Date(val).toLocaleString(undefined, {
      month: 'short', day: 'numeric', year: 'numeric',
      hour: 'numeric', minute: '2-digit'
    });
  };

  const getEventBadgeColor = (type: string) => {
    switch (type) {
      case 'Embassy': return 'bg-amber-50 border-amber-250 text-amber-700 hover:bg-amber-100';
      case 'Interview': return 'bg-blue-50 border-blue-200 text-brand hover:bg-blue-100';
      case 'Orientation': return 'bg-purple-50 border-purple-200 text-purple-700 hover:bg-purple-100';
      case 'Training': return 'bg-green-50 border-green-200 text-green-700 hover:bg-green-100';
      default: return 'bg-slate-50 border-slate-200 text-slate-700 hover:bg-slate-100';
    }
  };

  return (
    <div className="space-y-6 text-slate-800 animate-fade-in">
      {/* Header section */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-slate-200 pb-4">
        <div>
          <h1 className="text-xl font-bold text-slate-900 tracking-tight">Events & Calendar</h1>
          <p className="text-sm text-slate-500">Upcoming events, interviews, and orientations for your referred candidates</p>
        </div>
        <div className="flex items-center space-x-1 border border-slate-200 bg-slate-50 p-1 rounded-xl shrink-0">
          <button
            onClick={() => setViewModeChoice('calendar')}
            className={`px-3 py-3.5 md:py-2 text-xs font-bold rounded-lg transition-all cursor-pointer flex items-center space-x-1 ${
              eventViewMode === 'calendar' ? 'bg-white text-brand shadow-sm' : 'text-slate-600 hover:text-slate-950'
            }`}
          >
            <Calendar className="h-3.5 w-3.5" />
            <span>Calendar</span>
          </button>
          <button
            onClick={() => setViewModeChoice('list')}
            className={`px-3 py-3.5 md:py-2 text-xs font-bold rounded-lg transition-all cursor-pointer flex items-center space-x-1 ${
              eventViewMode === 'list' ? 'bg-white text-brand shadow-sm' : 'text-slate-600 hover:text-slate-950'
            }`}
          >
            <List className="h-3.5 w-3.5" />
            <span>List View</span>
          </button>
        </div>
      </div>

      {/* Filter and search controls */}
      <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm space-y-3">
        <div className="flex flex-col md:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
            <input
              type="text"
              aria-label="Search events by title, candidate name, or location"
              value={eventSearchQuery}
              onChange={(e) => setEventSearchQuery(e.target.value)}
              placeholder="Search by event title, candidate name, location..."
              className="w-full bg-slate-50 border border-slate-200 rounded-xl pl-9 pr-12 md:pr-10 py-3 md:py-2 text-base md:text-xs outline-none focus:bg-white focus:border-brand transition-all"
            />
            {eventSearchQuery && (
              <button
                onClick={() => setEventSearchQuery('')}
                aria-label="Clear search"
                className="absolute right-0 top-1/2 -translate-y-1/2 h-11 w-11 md:h-8 md:w-8 flex items-center justify-center cursor-pointer"
              >
                <span className="h-5 w-5 bg-slate-200 hover:bg-slate-300 text-slate-600 rounded-full flex items-center justify-center text-xs">
                  <X className="h-3 w-3" />
                </span>
              </button>
            )}
          </div>
          <div className="grid grid-cols-2 md:flex items-center gap-3">
            <select
              aria-label="Filter by event type"
              value={filterEventType}
              onChange={(e) => setFilterEventType(e.target.value)}
              className="max-md:w-full max-md:min-w-0 px-2.5 py-3 md:py-1.5 bg-slate-50 border border-slate-200 rounded-lg text-base md:text-xs font-bold outline-none focus:bg-white"
            >
              <option value="All">All Types</option>
              <option value="Embassy">Embassy</option>
              <option value="Interview">Interview</option>
              <option value="Orientation">Orientation</option>
              <option value="Training">Training</option>
              <option value="Other">Other</option>
            </select>
            <select
              aria-label="Filter by event status"
              value={filterEventStatus}
              onChange={(e) => setFilterEventStatus(e.target.value)}
              className="max-md:w-full max-md:min-w-0 px-2.5 py-3 md:py-1.5 bg-slate-50 border border-slate-200 rounded-lg text-base md:text-xs font-bold outline-none focus:bg-white"
            >
              <option value="All">All Statuses</option>
              <option value="Scheduled">Scheduled</option>
              <option value="Completed">Completed</option>
              <option value="Cancelled">Cancelled</option>
            </select>
          </div>
        </div>
      </div>

      {eventsLoading ? (
        <div className="flex items-center justify-center py-12"><div className="premium-loader" /></div>
      ) : filteredEvents.length === 0 ? (
        <div className="bg-white rounded-2xl shadow-sm ring-1 ring-slate-100 p-12 text-center border border-slate-200">
          <Calendar className="h-10 w-10 text-slate-300 mx-auto mb-3" />
          <p className="text-sm font-semibold text-slate-700">No events found</p>
          <p className="text-xs text-slate-500 mt-1">Try modifying your search or filters.</p>
        </div>
      ) : eventViewMode === 'list' ? (
        /* LIST VIEW */
        <div className="space-y-3">
          {filteredEvents.map((e: any) => (
            <div
              key={e.eventId}
              onClick={() => setSelectedEvent(e)}
              className="bg-white rounded-2xl p-5 shadow-sm ring-1 ring-slate-100 hover:shadow-md transition flex items-center justify-between border border-slate-200/50 cursor-pointer"
            >
              <div className="flex items-center space-x-4 min-w-0 flex-1">
                <div className={`p-3 rounded-xl ${e.eventType === 'Completed' ? 'bg-green-50' : 'bg-blue-50'}`}>
                  <Calendar className="h-5 w-5 text-brand" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="font-semibold text-slate-900 truncate">{e.title || 'Event'}</p>
                  <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-slate-500 mt-0.5">
                    <span className="flex items-center gap-1">
                      <Clock className="h-3 w-3 text-slate-400" />
                      {formatDateTime(e.dateTime)}
                    </span>
                    {e.location && (
                      <span className="flex items-center gap-1 truncate max-w-[200px]">
                        <MapPin className="h-3 w-3 text-slate-400" />
                        {e.location}
                      </span>
                    )}
                    {e.applicantName && (
                      <span className="flex items-center gap-1 font-medium text-slate-700">
                        <User className="h-3 w-3 text-brand/70" />
                        {e.applicantName}
                      </span>
                    )}
                  </div>
                </div>
              </div>
              <div className="flex items-center space-x-2 shrink-0 ml-3">
                <span className={`px-2.5 py-0.5 rounded-full text-[10px] font-bold border ${getEventBadgeColor(e.eventType)}`}>
                  {e.eventType.toUpperCase()}
                </span>
                <span className={`px-2.5 py-0.5 rounded-full text-[10px] font-bold border ${
                  e.status === 'Completed' ? 'bg-green-50 text-green-700 border-green-200' :
                  e.status === 'Cancelled' ? 'bg-red-50 text-red-700 border-red-200' :
                  'bg-amber-50 text-amber-700 border-amber-200'
                }`}>
                  {e.status.toUpperCase()}
                </span>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <SchedulingCalendar
          initialEvents={filteredEvents.map(ev => {
            const startTime = ev.dateTime || new Date().toISOString();
            const startDateObj = new Date(startTime);
            const endDateObj = new Date(startDateObj.getTime() + 60 * 60 * 1000); // default +1 hour duration
            const endTime = endDateObj.toISOString();

            const type = (ev.eventType || '').toLowerCase();
            const calendarId = ['embassy', 'interview', 'orientation', 'training', 'flight', 'medical', 'other'].includes(type)
              ? type
              : 'other';

            return {
              id: ev.eventId,
              title: ev.title,
              startTime,
              endTime,
              calendarId,
              status: ev.status,
              assignedTo: ev.applicantName || ev.assignedTo,
              location: ev.location,
              description: ev.description,
            };
          })}
          onEventChange={(updated) => {
            const matched = updated.map(up => {
              const matchOrig = events.find(o => o.eventId === up.id);
              if (matchOrig) {
                return {
                  ...matchOrig,
                  title: up.title,
                  dateTime: up.startTime,
                  location: up.location,
                  description: up.description,
                };
              }
              return {
                eventId: up.id,
                title: up.title,
                eventType: 'Interview',
                dateTime: up.startTime,
                status: 'Scheduled',
                location: up.location,
                description: up.description,
              };
            });
            setEvents(matched);
          }}
          // REF-14 role trace (SchedulingCalendar): userRole gates
          // exactly two things — the /api/admin/agents fetch (skipped
          // for non-admin) and the event popup's Delete + "Reschedule
          // & Edit" buttons (admin only). Passing "admin" here gave
          // referrers working-looking delete/edit affordances whose
          // saves only ever mutated this view's local state via
          // onEventChange below (nothing persists), plus a doomed
          // admin-agents fetch on every mount. The real role removes
          // both. Grid-cell click creation is not role-gated inside
          // SchedulingCalendar for any role (the employer surface
          // passes its real role the same way); gating it belongs to
          // the shared component, noted in the batch PR body.
          userRole="referrer"
          title="Your Calendar"
        />
      )}

      {/* EVENT DETAIL POPUP MODAL */}
      {selectedEvent && (
        <Modal onClose={() => setSelectedEvent(null)} ariaLabel="Event Details">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6 space-y-4">
            <div className="flex items-center justify-between border-b border-slate-100 pb-3">
              <div>
                <span className={`px-2.5 py-0.5 rounded-full text-[10px] font-bold border ${getEventBadgeColor(selectedEvent.eventType)}`}>
                  {selectedEvent.eventType}
                </span>
                <h3 className="text-sm font-bold text-slate-900 mt-1">{selectedEvent.title}</h3>
              </div>
              <button
                onClick={() => setSelectedEvent(null)}
                aria-label="Close event details"
                className="p-1.5 min-h-[44px] min-w-[44px] inline-flex items-center justify-center hover:bg-slate-100 rounded-lg text-slate-400 hover:text-slate-700 cursor-pointer"
              >
                <X className="h-4.5 w-4.5" />
              </button>
            </div>
            <div className="space-y-3.5 text-xs">
              <div className="flex items-start space-x-2.5">
                <Clock className="h-4 w-4 text-slate-400 mt-0.5" />
                <div>
                  <p className="font-semibold text-slate-700">Date & Time</p>
                  <p className="text-slate-500">{formatDateTime(selectedEvent.dateTime)} ({selectedEvent.timezone || 'UTC'})</p>
                </div>
              </div>
              {selectedEvent.location && (
                <div className="flex items-start space-x-2.5">
                  <MapPin className="h-4 w-4 text-slate-400 mt-0.5" />
                  <div>
                    <p className="font-semibold text-slate-700">Location</p>
                    <p className="text-slate-500">{selectedEvent.location}</p>
                  </div>
                </div>
              )}
              {selectedEvent.applicantName && (
                <div className="flex items-start space-x-2.5">
                  <User className="h-4 w-4 text-slate-400 mt-0.5" />
                  <div>
                    <p className="font-semibold text-slate-700">Candidate / Applicant</p>
                    <p className="text-slate-500">{selectedEvent.applicantName}</p>
                  </div>
                </div>
              )}
              {selectedEvent.description && (
                <div className="flex items-start space-x-2.5 border-t border-slate-100 pt-3">
                  <FileText className="h-4 w-4 text-slate-400 mt-0.5" />
                  <div>
                    <p className="font-semibold text-slate-700">Description</p>
                    <p className="text-slate-500 whitespace-pre-wrap leading-relaxed">{selectedEvent.description}</p>
                  </div>
                </div>
              )}
              <div className="flex items-center justify-between border-t border-slate-100 pt-3.5">
                <span className="text-slate-500 font-bold uppercase tracking-wider text-[10px]">Status</span>
                <span className={`px-2.5 py-0.5 rounded-full text-[10px] font-bold border ${
                  selectedEvent.status === 'Completed' ? 'bg-green-50 text-green-700 border-green-200' :
                  selectedEvent.status === 'Cancelled' ? 'bg-red-50 text-red-700 border-red-200' :
                  'bg-amber-50 text-amber-700 border-amber-200'
                }`}>
                  {selectedEvent.status.toUpperCase()}
                </span>
              </div>
            </div>
          </div>
        </Modal>
      )}

      {/* DATE POPUP MODAL (LIST OF EVENTS ON CELL CLICK) */}
      {datePopupDate && (
        <Modal onClose={() => { setDatePopupDate(null); setDatePopupEvents([]); }} ariaLabel="Events on Date">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-5 space-y-4">
            <div className="flex items-center justify-between border-b border-slate-100 pb-2">
              <h3 className="text-xs font-bold text-slate-700">
                Events on {datePopupDate.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })}
              </h3>
              <button
                onClick={() => { setDatePopupDate(null); setDatePopupEvents([]); }}
                aria-label="Close"
                className="p-1 min-h-[44px] min-w-[44px] inline-flex items-center justify-center hover:bg-slate-100 rounded text-slate-500 cursor-pointer"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="space-y-2 max-h-60 overflow-y-auto">
              {datePopupEvents.map(ev => (
                <div
                  key={ev.eventId}
                  onClick={() => {
                    setSelectedEvent(ev);
                    setDatePopupDate(null);
                    setDatePopupEvents([]);
                  }}
                  className="p-3 bg-slate-50 hover:bg-slate-100 border border-slate-200/60 rounded-xl cursor-pointer transition-all flex items-center justify-between"
                >
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-bold text-slate-800 truncate">{ev.title}</p>
                    <p className="text-[10px] text-slate-500">{new Date(ev.dateTime).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })}</p>
                  </div>
                  <span className={`px-2 py-0.5 rounded-full text-[11px] font-bold border shrink-0 ml-2 ${getEventBadgeColor(ev.eventType)}`}>
                    {ev.eventType}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
